import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "task";

/**
 * Which exercise is on screen, kept in the URL as `?task=3` (1-based) so a
 * refresh — or a shared link — lands on the same one.
 *
 * Reading is derived, not stored, so the URL is the single source of truth and
 * nothing rewrites it on load; only navigation updates it (via replace, so the
 * browser's Back button still leaves the session rather than stepping through
 * every exercise).
 *
 * The third element says whether the URL actually named a slide. Without it
 * „no `?task=`" and „`?task=1`" are the same 0, and a caller that wants to land
 * a returning learner somewhere else cannot tell a fresh arrival from a
 * deliberate link back to the first question.
 */
export function useSlideParam(
  count: number
): [number, (next: number | ((prev: number) => number)) => void, boolean] {
  const [searchParams, setSearchParams] = useSearchParams();

  const max = Math.max(0, count - 1);
  const parsed = Number.parseInt(searchParams.get(PARAM) || "", 10);
  const pinned = Number.isFinite(parsed) && parsed >= 1;
  const index = pinned ? Math.min(parsed - 1, max) : 0;

  const setIndex = useCallback(
    (next: number | ((prev: number) => number)) => {
      const resolved = typeof next === "function" ? next(index) : next;
      const clamped = Math.max(0, Math.min(resolved, max));
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(PARAM, String(clamped + 1));
          return params;
        },
        { replace: true }
      );
    },
    [index, max, setSearchParams]
  );

  return [index, setIndex, pinned];
}
